import { Vue } from "@/vue.setup"
import { makeModule } from "@/store/utils"
import { restrict, identity } from "@/utils/functions"
import { apiErrorHandler } from "@/utils/eh"
import { make } from "vuex-pathify"
import { asSequence } from "sequency"

const makeCMS = name => {
	const defaultTotal = 0;
	const defaultPerPage = 0;
	const defaultPage = 1;
	const defaultLastPage = defaultPage;
	const minPage = defaultPage;
	const isValidPage = (min, max, page) => min <= page && page <= max;



	const state = {
		name,
		loading: false,
		editing: false,
		creating: false,

		total: defaultTotal,
		perPage: defaultPerPage,
		page: defaultPage,
		lastPage: defaultLastPage,

		headers: [],

		pages: [],
		edit: {},
		errors: {},
	};

	const getters = {
		...make.getters(state),
		hasPageContent(state){
			const { page, pages, lastPage } = state;

			return isValidPage(minPage, lastPage, page)
			&& (page - 1) in pages
			&& Array.isArray(getters.pageContent(state));
		},
		contents(state){
			return asSequence(state.pages) //Sequence<Any>
			.filter(Array.isArray) //Sequence<Array<Content>>
			.flatMap(identity) //Sequence<Content>
			.toArray(); //Content[]
		},
		pageContent(state){
			return (state.page - 1) in state.pages
			? state.pages[state.page - 1]
			: null;
		},
		hasLoadedContent(state){
			return state.headers.length > 0 || (
				state.total !== defaultTotal
				&& state.perPage !== defaultPerPage
				&& state.page !== defaultPage
				&& state.lastPage !== defaultLastPage
			);
		},
		pageIsValid(state){
			return isValidPage(minPage, state.lastPage, state.page);
		},
		hasPreviousPage(state){
			return minPage < state.page;
		},
		hasNextPage(state){
			return state.page < state.lastPage;
		},
		isLoading(state){
			return state.loading;
		},
		isEditing(state){
			return state.editing;
		},
		isCreating(state){
			return state.creating;
		},
		hasErrorFor(state){
			return key => !!state.errors[key];
		},
	};

	const mutations = {
		...make.mutations(state),
		CHANGE_PAGE(state, newPage){
			if(!isValidPage(minPage, state.lastPage, newPage))
				return;

			mutations.SET_PAGE(state, newPage);
		},
		START_LOADING(state){
			state.loading = true;
		},
		STOP_LOADING(state){
			state.loading = false;
		},
		SET_PAGE_CONTENT(state, { page, content }){
			Vue.set(state.pages, page - 1, content);
			// state.pages[page] = content;
		},
		RESET_PAGE(state, page){
			if(!isValidPage(minPage, state.lastPage, page))
				return;

			if(page in state.pages)
				Vue.delete(state.pages, page);
		},
		UPDATE_EDIT_FIELD(state, { key, value, vm, skipCheck = false }){
			if(!skipCheck && !state.headers.includes(key)){
				vm.$toast.error("Tentative de modification frauduleuse ignorée");
				return;
			}

			if(state.edit !== null){
				Vue.set(state.edit, key, value);
				mutations.REMOVE_ERROR(state, key);
			}
		},
		RESET_EDIT(state){
			state.edit = {};
		},
		START_EDITING(state, { edit }){
			// debugger
			state.editing = true;
			Vue.set(state, "edit", {...edit});
			mutations.CLEAR_ERRORS(state);
		},
		STOP_EDITING(state){
			mutations.RESET_PAGE(state);
			state.editing = false;
		},
		UPDATE_CREATION_FIELD(state, payload){
			return mutations.UPDATE_EDIT_FIELD(state, payload);
		},
		START_CREATING(state){
			state.creating = true;
			/* const payload = asSequence(state)
			.map(key => ({ [key]: "" }))
			.reduce(Objet.assign); */
			const payload = {
				edit: {},
			};

			return mutations.START_EDITING(state, payload);
		},
		STOP_CREATING(state){
			state.creating = false;
			return mutations.STOP_EDITING(state);
		},
		SET_ERROR(state, { key, value }){
			Vue.set(state.errors, key, value);
			//state.errors[key] = value;
		},
		REMOVE_ERROR(state, key){
			mutations.SET_ERROR(state, {
				key,
				value: null,
			});
		},
		SET_ERRORS(state, errors){
			Object.entries(errors)
			.forEach(([key, value]) => mutations.SET_ERROR(state, {
				key,
				value,
			}));
		},
		CLEAR_ERRORS(state){
			Object.keys(state.errors)
			.forEach(key => mutations.REMOVE_ERROR(state, key))
		},
	}

	const actions = {
		async LOAD({ commit }, promiseFactory){
			commit("START_LOADING");
			try{
				await promiseFactory();
			}catch(e){
				commit("STOP_LOADING");
			}finally{
				commit("STOP_LOADING");
			}
		},
		LOAD_PAGE({ commit, getters, dispatch }, { page, vm }){
			return dispatch("LOAD", async () => {
				const oldPage = getters.page;

				commit("CHANGE_PAGE", page);
				if(!getters.hasPageContent){ // only load content if it's not already loaded
					try {
						const {
							paginated,
							headers,
						} = await vm.$json.get(`/api/${name}/${page}`);

						const {
							total,
							perPage,
							lastPage,
							data,
						} = paginated;

						if(!getters.hasLoadedContent){ // only set "metadata" on very first load
							commit("SET_HEADERS", headers);
							commit("SET_TOTAL", total);
							commit("SET_PER_PAGE", perPage);
							commit("SET_LAST_PAGE", lastPage);
						}

						if(getters.pageIsValid){
							commit("SET_PAGE_CONTENT", {
								page,
								content: data,
							});
						}
					} catch (e) {
						commit("CHANGE_PAGE", oldPage);
						vm.$toast.error(`Nous n'avons pas pu charger la page ${page} pour le contenu "${name}"`);
					}
				}
			});
		},
		LOAD_NEXT_PAGE({ dispatch, getters }, payload){
			return dispatch("LOAD_PAGE", {
				...payload,
				page: getters.page + 1,
			});
		},
		LOAD_PREV_PAGE({ dispatch, getters }, payload){
			return dispatch("LOAD_PAGE", {
				...payload,
				page: getters.page - 1,
			});
		},
		RELOAD_PAGE({ dispatch, commit, getters }, { vm, page }){
			commit("RESET_PAGE", page);

			return dispatch("LOAD_PAGE", {
				vm,
				page,
			});
		},
		async SAVE_EDIT({ dispatch, commit, getters }, { vm, create = false } = {}){
			if(!getters.isEditing)
				return;

			const payload = getters.edit;//restrict(getters.edit).to(getters.headers);
			try{
				if(create){
					try{
						// Create
						await vm.$json.post(
							`/api/${name}`,
							payload,
						);

						await Promise.all(
							asSequence(Object.keys(getters.pages))
							.map(parseInt)
							.map(x => x + 1)
							.map(page => dispatch("RELOAD_PAGE", {
								vm,
								page,
							})).toArray()
						);
					}catch(e){
						apiErrorHandler({
							e,
							vm,
							commit,
						});
					}
				}else{
					try{
						// Update
						const { id } = getters.edit;
						await vm.$json.post(
							`/api/${name}/${id}/update`,
							payload,
						);

						dispatch("RELOAD_PAGE", { //reload current to refresh displayed data
							vm,
							page: getters.page,
						});
					}catch(e){
						apiErrorHandler({
							e,
							vm,
							commit,
						});
					}
				}

				vm.$toast.success("Les données ont bien été sauvegardées");
				commit(create ? "STOP_CREATING" : "STOP_EDITING");
			}catch(e){
				vm.$toast.error("Nous n'avons pas pu sauvegarder les données, veuillez réessayer plus tard");
				throw e;
			}
		},
		DISCARD_EDIT({ commit, getters }){
			if(!getters.isEditing)
				return;

			commit("STOP_EDITING");
		},
		SAVE_CREATION({ dispatch }, { vm }){
			return dispatch("SAVE_EDIT", {
				vm,
				create: true,
			});
		},
		DISCARD_CREATION({ commit, getters }){
			if(!getters.isCreating)
				return;

			commit("STOP_CREATING");
		},
		async DELETE({ commit, dispatch, getters }, { vm }){
			if(!getters.isEditing)
				return;

			const payload = getters.edit; //restrict(getters.edit).to(getters.headers);
			const { id } = payload;

			try{
				await vm.$json.post(`/api/${name}/${id}/delete`, payload);

				dispatch("RELOAD_PAGE", {
					vm,
					page: getters.page,
				});

				vm.$toast.success(`Nous avons supprimé l'élément #${id} du contenu '${name}'`);
			}catch(e){
				apiErrorHandler({
					e,
					vm,
					commit,
				});
			}
		},
	};

	return makeModule({
		state,
		getters,
		mutations,
		actions,
	});
};

const cms_users = makeCMS("user");
const cms_diameters = makeCMS("diameter");
const cms_norms = makeCMS("norm");
const cms_taps = makeCMS("tap");

export {
	cms_users,
	cms_diameters,
	cms_norms,
	cms_taps,
}